import React from 'react';
import './CheckinV1/styles.css';
import {
  Flex,
  View,
  Text,
  Heading,
  Spinner,
} from '@smarterservices/smarter-ui';
import { Menu, Button, Mask } from '@instructure/ui';
import { useEffect, useState, useRef, useMemo } from 'react';
import { getStore, useCheckinStore, useExamSessionStore, useAppStore } from '../../stores/StoreContext';
import { Widget } from '@typeform/embed-react'
import ApproveDenyControls from './CheckinV1/ApproveDenyControls';
import Loader from '@ui/context/session/components/Common/Loader';

/**
 * Checkin Component
 * 
 * Manages the check-in process for proctored exam sessions. Displays different UI states
 * based on connection status, journey step, and check-in progress. Integrates with Typeform
 * for the proctor review process.
 * 
 * @component
 * 
 * @description
 * This component handles multiple states during the check-in process:
 * - **Not Connected**: Shows loading spinner while connecting to session
 * - **Cannot Start**: Displays message when user is not on required steps (WaitingForProctor or ConnectedToProctor)
 * - **Not Started**: Shows "Start Check In" button when connected but check-in hasn't begun
 * - **Active Check-in**: Displays Typeform widget and approve/deny controls
 * 
 * The Typeform widget is memoized to prevent re-rendering and form resets when parent
 * component state changes.
 * 
 * @returns {JSX.Element} The check-in interface with appropriate state-based UI
 * 
 * @example
 * // Used within SessionDetails component
 * <Checkin />
 */
const Checkin = () => {
  const CheckinStore = getStore('checkinStore');
  const AppStore = getStore('appStore');
  const ExamSessionStore = getStore('examSessionStore');

  const [isStarting, setIsStarting] = useState(false);

  // Subscribe to the specific values from the store to prevent unnecessary re-renders
  const isApproved = useCheckinStore((state) => state.isApproved);
  const isCheckinStarted = useCheckinStore((state) => state.isCheckinStarted);
  const proctorReviewProcessSid = useExamSessionStore((state) => state.sessionData.proctorReviewProcessSid);
  const journey = useExamSessionStore((state) => state.journey);
  const isRoomConnected = useAppStore((state) => state.isRoomConnected);
  const [canStartCheckin, setCanStartCheckin] = useState(true);
  const [currentStep, setCurrentStep] = useState(null);
  const checkinDrawerRef = useRef(null);

  // Memoize the Typeform Widget to prevent re-rendering and form resets
  const typeformWidget = useMemo(() => {
    if (!proctorReviewProcessSid) {
      return (
        <View padding="medium" textAlign="center">
          <Text>No proctor review process configured</Text>
        </View>
      );
    }

    return (
      <Widget
        id={proctorReviewProcessSid}
        onReady={() => console.log('Typeform Loaded and Ready')}
        onSubmit={(data) => console.log(data)}
        //onQuestionChanged={(data) => console.log(data)}
        autoResize={true}
        container={checkinDrawerRef.current}
        keepSession={true}
        hideFooter={true}
        hidden={{
          'proctorFirstName': AppStore?.user?.firstName,
          'proctorLastName': AppStore?.user?.lastName,
          'identityVerificationStatus': 'approved',
          'hasSecondCamera': true,
          'requireRoomScan': true,
        }}
      />
    );
  }, [proctorReviewProcessSid, AppStore?.user?.firstName, AppStore?.user?.lastName]);

  useEffect(() => {

    const startCheckinIfNeeded = async () => {
      const currentStep = ExamSessionStore.getCurrentJourneyStep();

      setCurrentStep(currentStep);

      if (!['WaitingForProctor', 'ConnectedToProctor'].includes(currentStep?.stepId) && !isCheckinStarted) {
        setCanStartCheckin(false);
      } else if (['WaitingForProctor'].includes(currentStep?.stepId) && isRoomConnected) {
        setCanStartCheckin(true);
      } else if (['ConnectedToProctor'].includes(currentStep?.stepId) && isRoomConnected) {
        await CheckinStore.startCheckin();
        setCanStartCheckin(true);
      }
    };

    if (isRoomConnected) {
      startCheckinIfNeeded();
    }
  }, [journey, isRoomConnected]);

  const renderApprovalControl = () => {
    return (
      <>
        {!isApproved && (
          <View as="div" className="session-checkin-controls">
            <ApproveDenyControls />
          </View>
        )}
      </>
    );
  };

  if (!isRoomConnected) {
    return (
      <Loader
        spinnerSize="medium"
        color="primary"
        textSize="large"
        text="Connecting to the session..."
      />
    );
  }

  if (!canStartCheckin) {
    return (
      <Flex as="div" height="100%" direction="column" padding="large none none none">
        <View as="div" className="checkin-not-started" maxWidth="650px" width="50%" minWidth="500px" shadow='above' padding="large" margin="none auto" borderRadius='small'>
          <Heading level="h3">Checkin can start once the test taker completes all required steps.</Heading>
          <View as="div" margin="large 0">
            <View as="p">
              <Text>Current Step: <strong>{currentStep?.name}</strong></Text>
            </View>
          </View>
        </View>
      </Flex>
    )
  }

  if (!isCheckinStarted) {
    return (
      <Flex as="div" height="100%" direction="column" padding="large none none none">
        <View as="div" className="checkin-not-started" maxWidth="650px" width="50%" minWidth="500px" shadow='above' padding="large" margin="none auto" borderRadius='small'>
          <Heading level="h3">You're connected to the test taker, but they have not been notified yet.</Heading>
          <View as="div" margin="small 0">
            <View as="p">
              <Text>Review the session information and then when ready, begin the check-in process.</Text>
            </View>
            <View as="p" margin="small 0 0 0">
              <Text>Once you click Start Check In, the test taker will be notified and you enable text and/or audio communications.</Text>
            </View>
          </View>
          <View as="div" textAlign='center' margin="medium 0 0 0">
            <Button
              color="success"
              disabled={isStarting}
              onClick={async () => {
                setIsStarting(true);
                try {
                  await CheckinStore.startCheckin();
                } catch (error) {
                  console.error('Error starting checkin:', error);
                } finally {
                  setIsStarting(false);
                }
              }}
              renderIcon={isStarting ? <Spinner size="x-small" renderTitle="Starting..." variant="inverse" themeOverride={{ inverseColor: 'white' }} /> : null}
            >
              {isStarting ? 'Starting...' : 'Start Check In'}
            </Button>
          </View>
        </View>
      </Flex>
    )
  }

  return (
    <View
      position="relative"
      margin="none none none none"
      padding="none"
      elementRef={(el) => {
        checkinDrawerRef.current = el;
      }}
    >
      {renderApprovalControl()}
      {typeformWidget}
    </View>
  );
};

export default Checkin;
